import React, { useState, useEffect, CSSProperties, RefObject } from 'react';
import Link from 'next/link';
import styles from './header.module.scss';
import Skeleton from 'react-loading-skeleton';
import MegaNavDesktop from './navigation/mega-nav-desktop';
import MegaNavMobile from './navigation/mega-nav-mobile-new';
// import LanguageSelector from '../ui/language-selector';
import SearchBar from '../forms/search-bar';
import { HeaderRes, NavigationMenu } from '../../typescript/response';
import { useRouter } from 'next/router';
import { getCachedAsset } from '../../helper/utils';
import { Button, LanguageSelector } from '../ui';
import SearchBarMobile from '../forms/search-bar-mobile';
import { usePageState } from '../../context/pageContext';
import { ResponsiveDisplay } from './responsive';

export default function Header({
  ref,
  header,
  navMenu,
}: {
  ref?: RefObject<HTMLDivElement | null>
  header: HeaderRes;
  navMenu: NavigationMenu[];
}) {
  const [isNavOpen, setIsNavOpen] = usePageState('isNavOpen');
  const [isSearchOpen, setIsSearchOpen] = usePageState('isSearchOpen');
  const [isHovered, setIsHovered] = useState(false);
  // const [isNavOpen, setIsNavOpen] = useState(false);
  // const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMenuLevelTwoOpened, setIsMenuLevelTwoOpened] = useState(false);
  const { locale, defaultLocale } = useRouter();
  const isDefaultLocale = locale === defaultLocale;
  const homeHref = isDefaultLocale ? '/' : `/${locale}`;
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);

  const closeAllModal = () => {
    setIsNavOpen(false);
    setIsSearchOpen(false);
  };

  const handleLocaleChange = (_newLocale: string) => {
    closeAllModal();
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      if (window.innerWidth >= 1200 && isNavOpen) {
        setIsNavOpen(false);
        setIsMenuLevelTwoOpened(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isNavOpen]);

  const appendHeightParam = (url: string, height: number) => {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('height', height.toString());
      return urlObj.toString();
    } catch {
      return url;
    }
  };

  const fullPill: CSSProperties = {
    backgroundColor: isHovered ? 'transparent' : 'var(--primary-500)',
    color: isHovered ? 'var(--primary-500)' : 'var(--white)',
    border: '1px solid var(--primary-500)',
    borderRadius: 'var(--br-base)',
    boxSizing: 'border-box',
    cursor: 'pointer',
    transition: 'background-color 300ms ease-out, color 300ms ease-out',
    textAlign: 'center',
    textDecoration: 'none',
    flex: '1',
    lineHeight: '20px',
    justifyContent: 'center',
    padding: '3.5px var(--xs)',
    fontSize: 'var(--small-size)',
    width: '102px',
  };

  return (
    <>
      <header
        ref={ref}
        className={styles.navWrap}
      >
        <div className={styles.container}>
          <div className={styles.logoDark}>
            {isMenuLevelTwoOpened ? (
              <button className={styles.backButton} onClick={() => setIsMenuLevelTwoOpened(false)}>
                ← Back
              </button>
            ) : header.logo ? (
              <Link href={homeHref} onClick={closeAllModal}>
                <img
                  src={getCachedAsset(appendHeightParam(header.logo.url, 55))}
                  alt={header.logo.title}
                  className="logo"
                  loading="lazy"
                />
              </Link>
            ) : (
              <Skeleton width={200} />
            )}
          </div>
          <div className={styles.utilityNav}>
            {header.search_page?.[0]?.url && (<>
              <button
                className={`${styles.searchBarTrigger}${isSearchOpen ? ` ${styles.searchBarTriggerActive}` : ''}`}
                onClick={() => {
                  setIsSearchOpen((prev) => !prev);
                }}
              ></button>
              <SearchBar
                search_page={header.search_page[0].url}
                search_placeholder={header.search_placeholder}
              />
            </>)}
            {header.request_quote?.page_reference?.[0]?.url && (<>
              <Button
                text={header.request_quote.label}
                showIcon
                propWidth="auto"
                propHeight="40px"
                className={styles.utilityNavButton}
                propBackgroundColor={`var(--brand-primary-500)`}
                propHoverBackgroundColor="transparent"
                propTextColor="var(--white)"
                link={`${isDefaultLocale ? '' : `/${locale}`}${header.request_quote.page_reference[0].url}${header.request_quote.form_type ? `?form_type=${header.request_quote.form_type}` : ''}`}
              />
            </>)}
            {header.metal_selector?.page_reference?.[0]?.url && (
              <Button
                text={header.metal_selector?.label ? header.metal_selector?.label : `Material Selector`}
                showIcon
                propWidth="auto"
                propHeight="40px"
                className={styles.utilityNavButton}
                propBackgroundColor={`var(--brand-secondary-500)`}
                propHoverBackgroundColor="transparent"
                propTextColor="var(--white)"
                link={header.metal_selector?.page_reference?.[0]?.url}
              />
            )}

            {header.show_language_selector && (
              <LanguageSelector
                languages={header.languages}
                currentLocale={locale || 'en-us'}
                onLocaleChange={handleLocaleChange}
                hideButton={true}
                header_strings={header.header_strings}
              />
            )}
          </div>
          <div className={styles.desktopNavWrapper}>
            <MegaNavDesktop
              navMenu={navMenu}
              currentLocale={locale || 'en-us'}
              showLanguageSelector={false}
              languages={header.languages}
              onLocaleChange={handleLocaleChange}
              // locations={header.locations}
              request_quote={header.request_quote}
              // contact_us={header.contact_us}
              header_strings={header.header_strings}
            />
          </div>
          <div className={styles.mobileNavWrapper}>
            <MegaNavMobile
              isOpen={isNavOpen}
              navMenu={navMenu}
              showLanguageSelector={header.show_language_selector}
              languages={header.languages}
              currentLocale={locale || 'en-us'}
              onLocaleChange={handleLocaleChange}
              locations={header.locations}
              request_quote={header.request_quote}
              metal_selector={header.metal_selector}
              contact_us={header.contact_us}
            />
          </div>
          {isNavOpen && (
            <LanguageSelector
              className={styles.mobileMenuHeaderLanguages}
              languages={header.languages}
              currentLocale={locale || 'en-us'}
              onLocaleChange={handleLocaleChange}
              hideButton={true}
            />
          )}

          <div className={styles.sideNav}>
            <div className={styles.top}>
              
            </div>

            <div className={styles.bottom}>
              {header.contact_us?.page_reference?.[0]?.url && (
                <Link
                  href={`${isDefaultLocale ? '' : `/${locale}`}${header.contact_us.page_reference[0].url}${header.contact_us.form_type ? `?form_type=${header.contact_us.form_type}` : ''}`}
                  style={fullPill}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {header.contact_us.label}
                </Link>
              )}
            </div>
          </div>

          <div className={`${styles.hamburgerMenu}${isNavOpen ? ` ${styles.hamburgerMenuOpen}` : ''}`}>
            {header.search_page?.[0]?.url && (
              // <SearchBarMobile
              //   search_page={header.search_page[0].url}
              //   isVisible={isSearchOpen}
              //   search_placeholder={header.search_placeholder}
              //   toggleVisibility={() => setIsSearchOpen(!isSearchOpen)}
              // />
              <button
                className={`${styles.searchBarTrigger}${isSearchOpen ? ` ${styles.searchBarTriggerActive}` : ''}`}
                onClick={() => {
                  setIsSearchOpen((prev) => !prev);
                }}
              ></button>
            )}
            <button
              className={`${styles.hamburgerMenuIcon}`}
              onClick={() => setIsNavOpen(!isNavOpen)}
            >
              <span></span><span></span><span></span><span></span>
            </button>
          </div>

          <ResponsiveDisplay
            breakpointDown="xl"
          >
            <SearchBarMobile
              search_page={header.search_page[0].url}
              isVisible={isSearchOpen}
              search_placeholder={header.search_placeholder}
              toggleVisibility={() => setIsSearchOpen(!isSearchOpen)}
            />
          </ResponsiveDisplay>
        </div>
      </header>
    </>
  );
}
